import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { getStorageValue, setStorageValue } from "../storage";
import moment from "moment";
import { SettingsContext } from "../../contexts/settings";
import { usePrevious } from "./use-previous";

export const useUserAdvancedFilters = (key) => {
    const expirationTime = useRef();
    const advancedFiltersTimeout = useRef();
    const [externalFilters, setExternalFilters] = useState(null);
    const prevExternalFilters = usePrevious(externalFilters);
    const [clearFilters, setClearFilters] = useState(false);
    const [usedUserFiltersConfig, setUsedUserFiltersConfig] = useState({
        filters: [],
    });
    const {
        settings: { globalSettings },
    } = useContext(SettingsContext);

    const getStoredFilters = () => {
        const storedAdvancedFilters = getStorageValue(key);

        return storedAdvancedFilters && storedAdvancedFilters.filters;
    };

    useEffect(() => {
        if (!expirationTime.current) {
            return;
        }

        if (advancedFiltersTimeout.current) {
            clearTimeout(advancedFiltersTimeout.current);
        }

        const isFilterExpired = expirationTime.current - Date.now() <= 0;
        const timeout = isFilterExpired ? 0 : expirationTime.current - Date.now();

        advancedFiltersTimeout.current = setTimeout(() => {
            handleClearAdvancedFilters();
        }, timeout);

        return () => {
            clearTimeout(advancedFiltersTimeout.current);
        };
    }, [usedUserFiltersConfig]);

    // apply filters on page change
    useEffect(() => {
        const storedFilters = getStoredFilters(key);

        if (!!storedFilters) {
            handleApplyExternalFilters(storedFilters, true);
        } else {
            // reset filters if nothing is stored on page change
            handleApplyExternalFilters();
        }
    }, [key]);

    const setUserFilters = useCallback(
        (filters, skipExpirationTimeSetting) => {
            const storedAdvancedFilters = getStorageValue(key);

            if (skipExpirationTimeSetting && storedAdvancedFilters && storedAdvancedFilters.expirationTime) {
                expirationTime.current = storedAdvancedFilters.expirationTime;
            } else {
                expirationTime.current = moment(new Date()).add(globalSettings.appliedFiltersLocalStorageExpirationInMinutes, "minute").valueOf();
            }

            const newUserFilters = {
                filters,
                expirationTime: expirationTime.current,
            };

            setUsedUserFiltersConfig(newUserFilters);
            setStorageValue(key, newUserFilters);
        },
        [globalSettings, key]
    );

    const clearUserFilters = useCallback(() => {
        advancedFiltersTimeout.current = undefined;
        expirationTime.current = undefined;
        setUsedUserFiltersConfig({
            filters: [],
        });
        localStorage.removeItem(key);
    }, [key]);

    const handleClearAdvancedFilters = useCallback(() => {
        clearUserFilters();
        setExternalFilters(null);
        setClearFilters(true);
    }, [clearUserFilters, setClearFilters]);

    const handleApplyExternalFilters = useCallback(
        (filters, skipExpirationTimeSetting = false, handleCloseAdvancedFilters) => {
            // if user applied no filters, reset to default
            if (!filters) {
                handleCloseAdvancedFilters && handleCloseAdvancedFilters();

                return handleClearAdvancedFilters();
            }

            setUserFilters(filters, skipExpirationTimeSetting);
            setExternalFilters(filters);
            setClearFilters(false);
            handleCloseAdvancedFilters && handleCloseAdvancedFilters();
        },
        [setUserFilters, handleClearAdvancedFilters, setClearFilters]
    );

    return {
        userFilters: usedUserFiltersConfig.filters,
        expirationTime: usedUserFiltersConfig.expirationTime,
        clearAdvancedFilters: handleClearAdvancedFilters,
        applyExternalFilters: handleApplyExternalFilters,
        externalFilters,
        clearFilters,
        prevExternalFilters,
    };
};
